/** Builds the Monthly Record unified timeline (spec item 12) purely from data each
 * section already fetched independently - never a new aggregation endpoint, never a
 * fabricated event. Every event is normalized to {timestamp, category, event_type, title,
 * summary, source_id, actor, status} and de-duplicated by (timestamp + title). */

const inPeriod = (isoTimestamp, period) => !!isoTimestamp && isoTimestamp.slice(0, 7) === period;

export function buildMonthlyTimeline({ period, schedule, availability, attendance, payroll, leaveLedger, training, medicalCheck, contract, notice }) {
  const events = [];

  (schedule?.assignments || []).forEach((a) => {
    events.push({
      timestamp: a.published_at || `${a.date}T00:00:00`,
      category: "Schedule", event_type: "SHIFT_PUBLISHED",
      title: `Shift published for ${a.date}`, summary: `${a.start_time}\u2013${a.end_time} at ${a.store_location || "-"}`,
      source_id: a.id, actor: a.published_by, status: a.schedule_status,
    });
  });

  [...(availability?.availability_submissions || []), ...(availability?.time_off_requests || [])].forEach((r) => {
    events.push({
      timestamp: r.reviewed_at || r.submitted_at || r.created_at,
      category: "Availability", event_type: r.status,
      title: `${r.request_type === "TIME_OFF" ? "Time-off" : "Availability"} request ${r.status?.toLowerCase()}`,
      summary: r.decision_note || r.notes || "", source_id: r.id, actor: r.reviewed_by, status: r.status,
    });
  });

  if (attendance?.approved_at) {
    events.push({
      timestamp: attendance.approved_at, category: "Attendance", event_type: "MONTH_APPROVED",
      title: "Timecard month approved", summary: `Status: ${attendance.official_record_status}`,
      source_id: attendance.pair?.id, actor: attendance.approved_by, status: attendance.official_record_status,
    });
  }
  if (attendance?.lock_timestamp) {
    events.push({
      timestamp: attendance.lock_timestamp, category: "Attendance", event_type: "MONTH_LOCKED",
      title: "Timecard locked for payroll", summary: "", source_id: attendance.pair?.id,
      actor: attendance.manager_approver, status: "LOCKED_FOR_PAYROLL",
    });
  }

  if (payroll?.payroll_line?.calculated_at) {
    events.push({
      timestamp: payroll.payroll_line.calculated_at, category: "Payroll", event_type: "PAYROLL_CALCULATED",
      title: "Payroll calculated", summary: `Gross \u00a5${Number(payroll.payroll_line.gross_pay || 0).toLocaleString()}`,
      source_id: payroll.payroll_line.id, actor: payroll.payroll_line.calculated_by, status: payroll.payroll_line.status,
    });
  }
  if (payroll?.payroll_line?.published_at) {
    events.push({
      timestamp: payroll.payroll_line.published_at, category: "Payroll", event_type: "PAYSLIP_PUBLISHED",
      title: "Payslip published", summary: "", source_id: payroll.payroll_line.id, status: "PUBLISHED",
    });
  }

  (leaveLedger || []).filter((t) => inPeriod(t.effective_date, period)).forEach((t) => {
    events.push({
      timestamp: t.effective_date, category: "Leave", event_type: t.type,
      title: `Leave ${t.type?.toLowerCase()} (${t.days > 0 ? "+" : ""}${t.days} day(s))`,
      summary: t.reason || "", source_id: t.id, actor: t.created_by, status: t.type,
    });
  });

  (training?.assignments || []).filter((a) => inPeriod(a.completed_at, period)).forEach((a) => {
    events.push({
      timestamp: a.completed_at, category: "Training", event_type: "COURSE_COMPLETED",
      title: `Completed: ${a.course_title || a.course_id}`, summary: "", source_id: a.id, status: a.status,
    });
  });
  (training?.certifications || []).filter((c) => inPeriod(c.issued_at, period)).forEach((c) => {
    events.push({
      timestamp: c.issued_at, category: "Training", event_type: "CERTIFICATION_ISSUED",
      title: `Certification issued: ${c.certification_name || c.certification_key}`, summary: "",
      source_id: c.id, status: c.status,
    });
  });

  (medicalCheck?.completion_history || []).filter((h) => inPeriod(h.completed_date, period)).forEach((h, i) => {
    events.push({
      timestamp: h.completed_date, category: "Compliance", event_type: "MEDICAL_CHECK_COMPLETED",
      title: "Medical check completed", summary: "", source_id: `medical-${i}`, status: "COMPLETED",
    });
  });

  if (contract?.employee_signature && inPeriod(contract.employee_signature.signed_at, period)) {
    events.push({
      timestamp: contract.employee_signature.signed_at, category: "Documents", event_type: "CONTRACT_SIGNED_EMPLOYEE",
      title: `Contract v${contract.version} signed by employee`, summary: "", source_id: contract.id, status: contract.status,
    });
  }
  if (contract?.company_signature && inPeriod(contract.company_signature.signed_at, period)) {
    events.push({
      timestamp: contract.company_signature.signed_at, category: "Documents", event_type: "CONTRACT_FULLY_EXECUTED",
      title: `Contract v${contract.version} fully executed`, summary: "", source_id: contract.id, status: contract.status,
    });
  }
  if (notice?.issued_at && inPeriod(notice.issued_at, period)) {
    events.push({
      timestamp: notice.issued_at, category: "Documents", event_type: "NOTICE_ISSUED",
      title: `Notice v${notice.version} issued`, summary: "", source_id: notice.id, status: notice.status,
    });
  }

  const seen = new Set();
  const deduped = events.filter((e) => {
    if (!e.timestamp) return false;
    const key = `${e.timestamp}|${e.title}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return deduped.sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0));
}
